/**
 * MessageMarkdownErrorBoundary
 *
 * Contains MathJax / Streamdown render failures so one bad TeX/unicode
 * character cannot blank the whole assistant message (PostHog:
 * mathjax-full BaseConfiguration `range[4]` on null range).
 *
 * Dependencies:
 * - React
 */

'use client'

import React, { Component, type ErrorInfo, type ReactNode } from 'react'

type Props = {
  children: ReactNode
  fallbackContent?: string
}

type State = {
  hasError: boolean
}

export class MessageMarkdownErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep console signal for support / PostHog breadcrumbs without rethrowing.
    console.error('[MessageMarkdown] render failed; showing plain fallback', {
      error,
      componentStack: info.componentStack
    })
  }

  componentDidUpdate(prevProps: Props) {
    // New markdown content — allow a fresh render attempt.
    if (
      this.state.hasError &&
      prevProps.fallbackContent !== this.props.fallbackContent
    ) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (this.state.hasError) {
      const text =
        typeof this.props.fallbackContent === 'string'
          ? this.props.fallbackContent
          : ''
      return (
        <pre className="whitespace-pre-wrap break-words font-sans text-sm text-foreground">
          {text}
        </pre>
      )
    }

    return this.props.children
  }
}
